import { useCallback, useEffect, useState } from "react";
import {
  Link,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Swal from "sweetalert2";
import { formatDate, getProfileImage } from "../../../core/api";
import { deleteAdmin, getAdmin, updateAdmin } from "../../../core/services/adminService";
import AppImage from "../../../shared/AppImage";
import {
  canManageAdmins,
  getAdminProfile,
} from "../../../core/adminAccess";
import { AdminAccountFields, AdminFormState } from "./adminFormShared";
import { formatAdminRole, roleTone } from "../../../shared/Utils";
import { buildPermissionState, getEnabledPermissionKeys } from "../../../core/adminPermissions";
import { PermissionPicker } from "./adminFormShared";

const buildFormFromAdmin = (admin: any): AdminFormState => ({
  name: admin.name || "",
  email: admin.email || "",
  password: "",
  role: admin.role === "support" ? "support" : "manager",
  isActive: admin.isActive !== false,
  permissions: buildPermissionState(admin.permissions || []),
});

const AdminDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const currentProfile = getAdminProfile();

  const [admin, setAdmin] = useState<any>(null);
  const [form, setForm] = useState<AdminFormState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  const isSuperAdminAccount = admin?.role === "superadmin";
  const isSelf = admin?._id === currentProfile?._id;
  const canEdit = canManageAdmins();
  const canEditThisAdmin = canEdit && !isSuperAdminAccount;
  const fetchAdmin = useCallback(async () => {
    if (!id) return;
    setIsLoading(true);
    try {
      const result = await getAdmin(id);
      if (!result.success || !result.admin) {
        throw new Error(result.message || "Admin not found");
      }

      setAdmin(result.admin);
      setForm(buildFormFromAdmin(result.admin));
      setIsEditing(false);
    } catch (error: any) {
      Swal.fire("Error", error.message || "Unable to load admin", "error");
      navigate("/admins");
    } finally {
      setIsLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    void fetchAdmin();
  }, [fetchAdmin]);

  useEffect(() => {
    if (searchParams.get("edit") === "1" && canEditThisAdmin && admin) {
      setIsEditing(true);
    }
  }, [searchParams, canEditThisAdmin, admin]);

  const updateForm = (updates: Partial<AdminFormState>) => {
    setForm((current) => (current ? { ...current, ...updates } : current));
  };

  const handleCancelEdit = () => {
    if (admin) {
      setForm(buildFormFromAdmin(admin));
    }
    setIsEditing(false);
    if (searchParams.get("edit") === "1") {
      navigate(`/admins/${admin._id}`, { replace: true });
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!form || !admin || !canEditThisAdmin || !isEditing) return;
    if (getEnabledPermissionKeys(form.permissions).length === 0) {
      Swal.fire("Permission required", "Select at least one module permission.", "warning");
      return;
    }

    setIsSaving(true);
    try {
      const payload: any = {
        name: form.name.trim(),
        email: form.email.trim(),
        role: form.role,
        isActive: form.isActive,
        permissions: form.permissions,
      };

      if (form.password.trim()) {
        payload.password = form.password.trim();
      }

      const result = await updateAdmin(admin._id, payload);
      if (!result.success)
        throw new Error(result.message || "Unable to update admin");

      await Swal.fire({
        title: "Admin Updated",
        text: "Account details saved successfully.",
        icon: "success",
        timer: 1400,
        showConfirmButton: false,
      });

      await fetchAdmin();
    } catch (error: any) {
      Swal.fire(
        "Update Failed",
        error.message || "Unable to update admin",
        "error",
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!admin || isSuperAdminAccount || isSelf || !canEditThisAdmin) return;

    const confirm = await Swal.fire({
      title: `Delete ${admin.name}?`,
      text: "This admin will lose panel access immediately.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#ef4444",
      confirmButtonText: "Delete Admin",
    });

    if (!confirm.isConfirmed) return;

    try {
      const result = await deleteAdmin(admin._id);
      if (!result.success)
        throw new Error(result.message || "Unable to delete admin");
      Swal.fire({
        title: "Deleted",
        icon: "success",
        timer: 1200,
        showConfirmButton: false,
      });
      navigate("/admins");
    } catch (error: any) {
      Swal.fire(
        "Delete Failed",
        error.message || "Unable to delete admin",
        "error",
      );
    }
  };

  if (isLoading) {
    return (
      <div className="page-section loading-state">
        <h2>Loading Admin Details…</h2>
      </div>
    );
  }

  if (!admin || !form) return null;

  const profileImage = getProfileImage(admin);

  return (
    <div className="detail-page">
      <div className="page-hdr">
        <div className="page-hdr-left">
          <Link to="/admins" className="btn-sm btn-outline back-link">
            ← Back to All Admins
          </Link>
          <div className="page-eyebrow">Admin Account</div>
          <h1 className="page-title">{admin.name}</h1>
          <div className="page-sub">
            {admin.email} · Joined {formatDate(admin.createdAt)}
          </div>
        </div>
        <div className="page-hdr-right">
          {isSuperAdminAccount && isSelf && (
            <Link to="/profile" className="btn-sm btn-primary">
              Edit Profile
            </Link>
          )}
          {canEditThisAdmin && !isEditing && (
            <button
              type="button"
              className="btn-sm btn-primary"
              onClick={() => setIsEditing(true)}
            >
              Edit Admin
            </button>
          )}
          {canEditThisAdmin && isEditing && (
            <>
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                form="admin-detail-form"
                className="btn-sm btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </>
          )}
          {canEditThisAdmin && !isSelf && !isEditing && (
            <button
              type="button"
              className="btn-sm btn-danger"
              onClick={handleDelete}
            >
              Delete Admin
            </button>
          )}
        </div>
      </div>

      <div className="detail-grid-2">
        <div className="table-card profile-card">
          <AppImage
            src={profileImage}
            alt={admin.name}
            crossOrigin="anonymous"
            className="profile-avatar-img"
          />

          <div>
            <div className="profile-name">{admin.name}</div>
            <div className="profile-email">{admin.email}</div>
          </div>

          <div className="profile-badge-row">
            <span className={`status-badge ${roleTone(admin.role)}`}>
              {formatAdminRole(admin.role)}
            </span>
            <span
              className={`status-badge ${admin.isActive ? "success" : "danger"}`}
            >
              {admin.isActive ? "Active" : "Inactive"}
            </span>
          </div>

          <div className="profile-meta">
            <div>
              <strong>Admin ID:</strong>
              <br />
              <span className="profile-meta-id">{admin._id}</span>
            </div>
            <div>
              <strong>Status:</strong> {admin.isActive ? "Active" : "Inactive"}
            </div>
            <div>
              <strong>Last Login:</strong> {formatDate(admin.lastLoginAt)}
            </div>
          </div>
        </div>

        <div className="page-stack">
          <section className="detail-metrics-row">
            {[
              { label: "Role", value: admin.role, icon: "🛡️", tone: "blue" },
              {
                label: "Last Login",
                value: formatDate(admin.lastLoginAt),
                icon: "🕒",
                tone: "orange",
              },
            ].map((metric) => (
              <article
                key={metric.label}
                className={`metric metric-${metric.tone}`}
              >
                <div className="metric-top">
                  <span className="metric-icon">{metric.icon}</span>
                  <div className="metric-label">
                    <p>{metric.label}</p>
                    <strong>{metric.value}</strong>
                  </div>
                </div>
              </article>
            ))}
          </section>

          {isSuperAdminAccount && (
            <div className="table-card info-card">
              <h3 className="info-card-title">Primary Super Admin</h3>
              <div className="info-list">
                <p>
                  This account has full access to every module in the admin
                  panel.
                </p>
                {isSelf && (
                  <p>
                    Profile details are managed from{" "}
                    <Link to="/profile">Profile</Link>.
                  </p>
                )}
              </div>
            </div>
          )}

          {canEditThisAdmin && !isEditing && (
            <div className="table-card info-card">
              <h3 className="info-card-title">Quick Actions</h3>
              <div className="info-list">
                <p>
                  Use <strong>Edit Admin</strong> to update account details,
                  role and status.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      {isEditing && canEditThisAdmin ? (
        <form
          id="admin-detail-form"
          onSubmit={handleSubmit}
          className="page-stack"
        >
          <section className="table-card">
            <div className="table-head">
              <h2>Edit Account Profile</h2>
              <p>Update admin profile, role and account status</p>
            </div>
            <div className="admin-card-body">
              <AdminAccountFields
                form={form}
                onChange={updateForm}
                passwordOptional
                disableActive={isSelf}
              />
            </div>
          </section>

          <PermissionPicker permissions={form.permissions} onChange={(permissions) => updateForm({ permissions })} />

          <div className="detail-footer">
            {!isSelf && (
              <button
                type="button"
                className="btn-sm btn-danger"
                onClick={handleDelete}
              >
                Delete Admin
              </button>
            )}
            <div className="detail-footer-right">
              <button
                type="button"
                className="btn-sm btn-outline"
                onClick={handleCancelEdit}
              >
                Cancel
              </button>
              <button
                type="submit"
                className="btn-sm btn-primary"
                disabled={isSaving}
              >
                {isSaving ? "Saving..." : "Save Changes"}
              </button>
            </div>
          </div>
        </form>
      ) : null}
    </div>
  );
};

export default AdminDetail;
